import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { Button, Flex, Layout, Spin, Tag, Typography } from 'antd';
import { CloseOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { getHelpCenterLink } from '@shared/utils';
import { useGlobals } from '@shared/contexts/GlobalsContext';
import { useAppSelector } from '@shared/hooks/use-app-selector';
import useMerchantSettings from '../../hooks/use-merchant-settings';
import { SIGNUP_SERVICE_AGENTS_BASE_URL } from '../../constants';
import { t } from '../../utils/i18n';
import createQueryConfig from '../../queries';
import { getAppSettings } from '../../selectors';
import { useAIAssistant } from '../../shared/contexts/AIAssistantContext';

const { Sider: AntdSider } = Layout;

const StyledAIAssistant = styled(AntdSider)`
  background-color: ${({ theme }) => theme.antd.colorBgLayout};
  width: 400px !important;
  flex: 0 0 400px !important;
  max-width: unset !important;
  height: 100%;
  border-inline-start: 1px solid #ededed;
  position: relative;
  z-index: 1001;
`;

const StyledPanel = styled(Flex)`
  height: 100%;
`;

const StyledHeader = styled(Flex)`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.antd.colorBorderSecondary};
  background-color: ${({ theme }) => theme.antd.colorBgContainer};
`;

const StyledSparkle = styled.span`
  color: ${({ theme }) => theme.antd.colorPrimary};
  line-height: 1;
`;

const StyledBetaTag = styled(Tag)`
  margin: 0;
  border: none;
  border-radius: 0.5rem;
  color: ${({ theme }) => theme.antd.colorPrimary};
  background: ${({ theme }) => theme.antd.colorPrimaryBg};
`;

const StyledIconButton = styled(Button)`
  color: ${({ theme }) => theme.antd.colorTextSecondary};
`;

const StyledBody = styled(Flex)`
  flex: 1;
  padding: 0;
  overflow-y: auto;
`;

const StyledLoadingContainer = styled(Flex)`
  height: 100%;
  min-height: 150px;
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  display: block;
`;

export function AIAssistant() {
  const [isLoading, setIsLoading] = useState(true);
  const { queryClient, apiClient, trackMethod } = useGlobals();
  const { closeAIAssistant, stepId } = useAIAssistant();

  const {
    company,
    whiteLabelConfiguration: { schemaless: whiteLabelSchemaless },
  } = useAppSelector(getAppSettings);

  const queryConfig = createQueryConfig(queryClient, apiClient);
  const { data: token } = useQuery(queryConfig.authToken());

  const { merchantSettings } = useMerchantSettings();
  const { organizationId, merchantId, locale } = merchantSettings || {};

  const aiAssistantHelpCenterArticleUrl = getHelpCenterLink({
    key: 'ai_assistant',
    config: whiteLabelSchemaless,
    locale,
    companyName: company,
  });

  const openAIAssistantHelpCenterArticle = () => {
    if (aiAssistantHelpCenterArticleUrl) {
      window.open(aiAssistantHelpCenterArticleUrl, '_blank');
      trackMethod('ai_assistant_help_center_icon_clicked');
    }
  };

  const isReady = Boolean(token && organizationId && merchantId && stepId);

  const iframeSrc = isReady
    ? `${SIGNUP_SERVICE_AGENTS_BASE_URL}/agents/merchant_onboarding/organizations/${organizationId}/merchants/${merchantId}/steps/${stepId}?locale=${locale}&token=${token}`
    : undefined;

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <StyledAIAssistant data-testid="ai-assistant">
      <StyledPanel vertical>
        <StyledHeader align="center" justify="space-between">
          <Flex align="center" gap={8}>
            <StyledSparkle style={{ fontSize: 20 }}>✦</StyledSparkle>
            <Flex vertical gap={2}>
              <Flex align="center" gap={8}>
                <Typography.Text strong>
                  {t('ai-assistant.title')}
                </Typography.Text>
                <StyledBetaTag>{t('ai-assistant.beta')}</StyledBetaTag>
              </Flex>
            </Flex>
          </Flex>
          <Flex align="center">
            {aiAssistantHelpCenterArticleUrl && (
              <StyledIconButton
                type="text"
                icon={<QuestionCircleOutlined />}
                aria-label={t('button.help-center.title')}
                onClick={openAIAssistantHelpCenterArticle}
              />
            )}
            <StyledIconButton
              type="text"
              icon={<CloseOutlined />}
              aria-label="close"
              onClick={closeAIAssistant}
            />
          </Flex>
        </StyledHeader>

        <StyledBody vertical gap={20}>
          {isReady && !isLoading ? (
            <StyledIframe
              src={iframeSrc}
              title="AI Assistant"
              referrerPolicy="no-referrer"
            />
          ) : (
            <StyledLoadingContainer align="center" justify="center">
              <Spin />
            </StyledLoadingContainer>
          )}
        </StyledBody>
      </StyledPanel>
    </StyledAIAssistant>
  );
}
